import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import type { EvaluationWeights, SupplierEvaluation } from '@provalia/shared';
import { Badge, Card, Empty, ErrorBox, Loading, errorText, useAsync, useToast } from '../../shared/ui';
import { fmtDateTime, fmtNumber } from '../../shared/format';
import { api } from '../api';
import { isBuyer, isSupplier, usePortal } from '../context';
import { SupplierLogo } from '../components/status';

interface StaffEvaluation {
  evaluations: SupplierEvaluation[];
  weights: EvaluationWeights;
  windowMonths: number;
}

interface SupplierView {
  own: SupplierEvaluation | null;
  ranking: Array<{ rank: number | null; total: number | null; manual: boolean; isMe: boolean }>;
  weights: EvaluationWeights;
  windowMonths: number;
}

type Key = keyof EvaluationWeights;

const INDICATORS: Array<{ key: Key; label: string; how: string }> = [
  { key: 'quality', label: 'Calidad', how: 'Líneas recibidas sin devolución sobre el total de líneas recibidas.' },
  { key: 'punctuality', label: 'Puntualidad', how: 'Líneas recibidas en la fecha comprometida o antes.' },
  { key: 'availability', label: 'Disponibilidad', how: 'Pedidos aceptados sobre pedidos respondidos (aceptados + rechazados).' },
  { key: 'agility', label: 'Agilidad', how: 'Días medios hasta responder a un pedido: 100 si responde en un día o menos, 0 a partir de 7 días.' },
];

/** Nota que cuenta: la fijada a mano manda. */
const effective = (e: { total: number | null; manual: boolean }): number | null => (e.manual ? 100 : e.total);

function Score({ value }: { value: number | null }): JSX.Element {
  if (value === null) return <span className="subtle">Sin datos</span>;
  const tone = value >= 80 ? 'ok' : value >= 50 ? 'warn' : 'bad';
  return (
    <span className={`pt-score ${tone}`}>
      <span className="pt-score-num">{fmtNumber(value, 1)}</span>
      <span className="pt-score-bar" aria-hidden="true">
        <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </span>
    </span>
  );
}

function HowItWorks({ weights, windowMonths }: { weights: EvaluationWeights; windowMonths: number }): JSX.Element {
  return (
    <Card title="Cómo se calcula">
      <div className="stack">
        <p className="muted">
          Se calcula con la actividad de los últimos {windowMonths} meses. Cada indicador va de 0 a 100 y la nota es su media ponderada. Un indicador
          «sin datos» (por ejemplo, sin entregas en el periodo) no cuenta ni como 0 ni como 100: se deja fuera y su peso se reparte entre los demás.
        </p>
        <dl className="kv">
          {INDICATORS.map((i) => (
            <Fragment key={i.key}>
              <dt>
                {i.label} · {fmtNumber(weights[i.key])} %
              </dt>
              <dd>{i.how}</dd>
            </Fragment>
          ))}
        </dl>
      </div>
    </Card>
  );
}

export function Evaluacion(): JSX.Element {
  const { me } = usePortal();
  return isSupplier(me.session.role) ? <SupplierEvaluationPage /> : <StaffEvaluationPage />;
}

// ── Vista del proveedor: su nota y su puesto, sin nombres ajenos ──────────

function SupplierEvaluationPage(): JSX.Element {
  const { me } = usePortal();
  const { data, error, reload } = useAsync(() => api.get<SupplierView>('/portal/evaluation'), []);

  if (error) return <ErrorBox error={error} onRetry={() => void reload()} />;
  if (!data) return <Loading />;
  const { own, ranking, weights, windowMonths } = data;
  const ranked = ranking.filter((r) => r.rank !== null).length;

  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <h1>Mi evaluación</h1>
          <p className="muted">Cómo valora {me.tenant.name} tu servicio. Los mejores proveedores reciben más carga de trabajo.</p>
        </div>
      </div>

      {!own ? (
        <Card>
          <Empty icon="📊">
            <strong>Todavía no hay evaluación calculada para tu empresa.</strong>
            <div>Se calcula periódicamente con tus pedidos, entregas y respuestas en el portal.</div>
          </Empty>
        </Card>
      ) : (
        <>
          <div className="grid cols-3 pt-stats">
            <div className="card stat">
              <div className="label">Tu nota</div>
              <div className="value">{effective(own) === null ? '—' : `${fmtNumber(effective(own), 1)}`}</div>
              <div className="foot">{own.manual ? 'Fijada al 100 % por compras' : 'sobre 100'}</div>
            </div>
            <div className="card stat">
              <div className="label">Tu puesto</div>
              <div className="value">{own.rank === null ? '—' : own.rank}</div>
              <div className="foot">{own.rank === null ? 'Sin datos suficientes' : `de ${ranked} proveedores`}</div>
            </div>
            <div className="card stat">
              <div className="label">Calculada</div>
              <div className="value pt-stat-small">{fmtDateTime(own.computedAt)}</div>
              <div className="foot">últimos {own.windowMonths} meses</div>
            </div>
          </div>

          <Card title="Desglose">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Indicador</th>
                    <th className="num">Peso</th>
                    <th>Nota</th>
                    <th>De dónde sale</th>
                  </tr>
                </thead>
                <tbody>
                  {INDICATORS.map((i) => (
                    <tr key={i.key}>
                      <td>
                        <strong>{i.label}</strong>
                      </td>
                      <td className="num">{fmtNumber(weights[i.key])} %</td>
                      <td>
                        <Score value={own[i.key]} />
                      </td>
                      <td className="subtle pt-wrap">{detailFor(i.key, own)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {ranking.length > 0 ? (
        <Card title="Ranking" bodyless>
          <p className="subtle pt-card-note">Los demás proveedores aparecen sin nombre.</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="num">Puesto</th>
                  <th>Proveedor</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((r, i) => (
                  <tr key={i} className={r.isMe ? 'pt-me' : ''}>
                    <td className="num">{r.rank ?? '—'}</td>
                    <td>{r.isMe ? <strong>Tu empresa</strong> : <span className="muted">Otro proveedor</span>}</td>
                    <td>
                      <Score value={effective(r)} />
                      {r.manual ? <Badge tone="accent">Fijada al 100 %</Badge> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <HowItWorks weights={weights} windowMonths={windowMonths} />
    </div>
  );
}

function detailFor(key: Key, e: SupplierEvaluation): string {
  const d = e.details;
  switch (key) {
    case 'quality':
      return d.receivedLines ? `${d.receivedLines - d.returnedLines} de ${d.receivedLines} líneas recibidas sin devolución` : 'Sin líneas recibidas en el periodo';
    case 'punctuality':
      return d.receivedLines ? `${d.onTimeLines} de ${d.receivedLines} líneas recibidas a tiempo` : 'Sin líneas recibidas en el periodo';
    case 'availability':
      return d.accepted + d.rejected ? `${d.accepted} pedidos aceptados y ${d.rejected} rechazados` : 'Sin pedidos respondidos en el periodo';
    case 'agility':
      return d.avgResponseDays === null ? 'Sin respuestas en el periodo' : `Responde en ${fmtNumber(d.avgResponseDays, 1)} días de media`;
  }
}

// ── Vista del personal: ranking completo ──────────────────────────────────

function StaffEvaluationPage(): JSX.Element {
  const { me } = usePortal();
  const buyer = isBuyer(me.session.role);
  const toast = useToast();
  const { data, error, reload, setData } = useAsync(() => api.get<StaffEvaluation>('/portal/evaluation'), []);
  const [running, setRunning] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);

  async function runNow(): Promise<void> {
    setRunning(true);
    try {
      const res = await api.post<{ evaluations: SupplierEvaluation[]; sapWarnings: string[] }>('/portal/evaluation/run');
      setWarnings(res.sapWarnings ?? []);
      if (data) setData({ ...data, evaluations: res.evaluations });
      else void reload();
      toast(`Evaluación recalculada: ${res.evaluations.length} proveedores`, 'ok');
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <h1>Evaluación de proveedores</h1>
          <p className="muted">Ranking por nota ponderada. Cada proveedor ve su nota y su puesto, pero no los nombres de los demás.</p>
        </div>
        {buyer ? (
          <div className="row">
            <Link className="btn" to="/proveedores">
              Fijar valoración al 100 %
            </Link>
            <button className="btn primary" onClick={() => void runNow()} disabled={running}>
              {running ? 'Recalculando…' : 'Recalcular ahora'}
            </button>
          </div>
        ) : null}
      </div>

      {warnings.length > 0 ? (
        <div className="callout warn">
          <div className="title">El cálculo ha terminado con avisos</div>
          <ul className="pt-list">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <Card title={data ? `Ranking · últimos ${data.windowMonths} meses` : 'Ranking'} bodyless>
        {error ? (
          <div className="card-body">
            <ErrorBox error={error} onRetry={() => void reload()} />
          </div>
        ) : !data ? (
          <Loading />
        ) : data.evaluations.length === 0 ? (
          <Empty icon="📊">
            <strong>Todavía no hay evaluación calculada.</strong>
            <div>{buyer ? 'Pulsa «Recalcular ahora» para calcularla con los datos de SAP y del portal.' : 'Compras la calculará.'}</div>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="num">Puesto</th>
                  <th>Proveedor</th>
                  <th>Nota</th>
                  {INDICATORS.map((i) => (
                    <th key={i.key}>
                      {i.label} <span className="pt-th-weight">{fmtNumber(data.weights[i.key])} %</span>
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.evaluations.map((e) => (
                  <Fragment key={e.cardCode}>
                    <tr>
                      <td className="num">
                        <strong>{e.rank ?? '—'}</strong>
                      </td>
                      <td>
                        <div className="row" style={{ flexWrap: 'nowrap' }}>
                          <SupplierLogo cardCode={e.cardCode} hasLogo name={e.cardName} />
                          <div>
                            <div>
                              <strong>{e.cardName}</strong>
                            </div>
                            <div className="subtle">{e.cardCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="nowrap">
                        <Score value={effective(e)} />
                        {e.manual ? (
                          <div>
                            <Badge tone="accent">Fijada al 100 %</Badge>
                          </div>
                        ) : null}
                      </td>
                      {INDICATORS.map((i) => (
                        <td key={i.key} className="nowrap">
                          <Score value={e[i.key]} />
                        </td>
                      ))}
                      <td className="nowrap">
                        <button className="btn sm ghost" aria-expanded={open === e.cardCode} onClick={() => setOpen(open === e.cardCode ? null : e.cardCode)}>
                          {open === e.cardCode ? 'Ocultar' : 'Detalle'}
                        </button>
                      </td>
                    </tr>
                    {open === e.cardCode ? (
                      <tr className="pt-detail-row">
                        <td colSpan={4 + INDICATORS.length}>
                          <dl className="kv">
                            {INDICATORS.map((i) => (
                              <Fragment key={i.key}>
                                <dt>{i.label}</dt>
                                <dd>{detailFor(i.key, e)}</dd>
                              </Fragment>
                            ))}
                            <dt>Calculada</dt>
                            <dd>{fmtDateTime(e.computedAt)}</dd>
                            {e.details.notes.length > 0 ? (
                              <>
                                <dt>Avisos</dt>
                                <dd>{e.details.notes.join(' · ')}</dd>
                              </>
                            ) : null}
                          </dl>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data ? <HowItWorks weights={data.weights} windowMonths={data.windowMonths} /> : null}
    </div>
  );
}
